import { Modal, Spin, Table, Tag } from 'antd';
import { contentService, seasonService } from 'content/service';
import React, { useCallback, useEffect, useState } from 'react';
import odkAdminApiService from 'service/odxAdminApiService';

type DistributionKind = 'season' | 'episode';

interface DistributionSet {
  service_name: string;
  // Season uses program_id + season_id; Episode/Content uses episode_id + content_id.
  program_id?: number;
  season_id?: number;
  episode_id?: number;
  content_id?: number;
}

interface ServiceRow {
  key: string;
  service: string;
  pk: number | null;
  loading: boolean;
  enabledLabel: React.ReactNode;
  error?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  kind: DistributionKind;
  recordId: number | null;
  recordTitle?: string;
}

const renderOdkTag = (enable: unknown) => {
  if (enable === true) return <Tag color="green">enable: true</Tag>;
  if (enable === false) return <Tag color="red">enable: false</Tag>;
  return <Tag>enable: —</Tag>;
};

const renderAmasianTag = (status: unknown) => {
  if (typeof status !== 'string' || !status) return <Tag>status: —</Tag>;
  const lower = status.toLowerCase();
  const color = lower === 'published' || lower === 'active' ? 'green' : 'orange';
  return <Tag color={color}>status: {status}</Tag>;
};

const DistributionStatusModal: React.FC<Props> = ({
  open,
  onClose,
  kind,
  recordId,
  recordTitle,
}) => {
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [rows, setRows] = useState<ServiceRow[]>([]);

  const updateRow = useCallback((key: string, patch: Partial<ServiceRow>) => {
    setRows(prev => prev.map(row => (row.key === key ? { ...row, ...patch } : row)));
  }, []);

  const loadServiceStatus = useCallback(
    async (row: ServiceRow, dist: DistributionSet) => {
      const service = dist.service_name?.toLowerCase();
      try {
        if (service === 'odk') {
          const pk = kind === 'season' ? dist.program_id : dist.episode_id;
          if (!pk) {
            updateRow(row.key, {
              loading: false,
              enabledLabel: <Tag>no ID</Tag>,
              error: 'Missing ODK id',
            });
            return;
          }
          const data =
            kind === 'season'
              ? await odkAdminApiService.fetchOdkProgram(pk)
              : await odkAdminApiService.fetchOdkEpisode(pk);
          updateRow(row.key, {
            loading: false,
            enabledLabel: renderOdkTag(data?.enable),
          });
          return;
        }

        if (service === 'amasian') {
          const pk = kind === 'season' ? dist.program_id : dist.episode_id;
          if (!pk) {
            updateRow(row.key, {
              loading: false,
              enabledLabel: <Tag>no ID</Tag>,
              error: 'Missing Amasian id',
            });
            return;
          }
          const data =
            kind === 'season'
              ? await odkAdminApiService.fetchAmasianSeason(pk)
              : await odkAdminApiService.fetchAmasianContent(pk);
          updateRow(row.key, {
            loading: false,
            enabledLabel: renderAmasianTag(data?.status),
          });
          return;
        }

        updateRow(row.key, {
          loading: false,
          enabledLabel: <Tag>not supported</Tag>,
        });
      } catch (e: any) {
        const msg = e?.response?.status
          ? `HTTP ${e.response.status}`
          : e?.message || 'Request failed';
        updateRow(row.key, {
          loading: false,
          enabledLabel: <Tag color="red">error</Tag>,
          error: msg,
        });
      }
    },
    [kind, updateRow]
  );

  useEffect(() => {
    if (!open || !recordId) return;

    let cancelled = false;
    setLoadingDetail(true);
    setDetailError(null);
    setRows([]);

    const run = async () => {
      try {
        const detail =
          kind === 'season'
            ? await seasonService.fetchById(recordId)
            : await contentService.fetchById(recordId);
        if (cancelled) return;

        const distributionSets: DistributionSet[] = (detail as any)?.distribution_sets ?? [];

        if (distributionSets.length === 0) {
          setRows([]);
          setLoadingDetail(false);
          return;
        }

        const initialRows: ServiceRow[] = distributionSets.map((dist, idx) => {
          const pk = kind === 'season' ? dist.program_id : dist.episode_id;
          return {
            key: `${dist.service_name}-${idx}`,
            service: dist.service_name,
            pk: pk ?? null,
            loading: true,
            enabledLabel: <Spin size="small" />,
          };
        });
        setRows(initialRows);
        setLoadingDetail(false);

        await Promise.allSettled(
          initialRows.map((row, idx) => loadServiceStatus(row, distributionSets[idx]))
        );
      } catch (e: any) {
        if (cancelled) return;
        setDetailError(e?.message || 'Failed to load detail');
        setLoadingDetail(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, recordId, kind, loadServiceStatus]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      title={`Distribution Status${recordTitle ? ` — ${recordTitle}` : ''}`}
      width={640}
      footer={null}
      destroyOnClose
    >
      {loadingDetail ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : detailError ? (
        <div style={{ color: '#cf1322' }}>{detailError}</div>
      ) : rows.length === 0 ? (
        <div style={{ color: '#666' }}>No distribution sets registered for this item.</div>
      ) : (
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={rows}
          columns={[
            { title: 'Service', dataIndex: 'service', key: 'service', width: 120 },
            { title: 'ID', dataIndex: 'pk', key: 'pk', width: 100, render: v => v ?? '—' },
            {
              title: 'Status',
              dataIndex: 'enabledLabel',
              key: 'enabledLabel',
              render: (v, record) => (record.loading ? <Spin size="small" /> : v),
            },
            {
              title: 'Error',
              dataIndex: 'error',
              key: 'error',
              render: v => (v ? <span style={{ color: '#cf1322' }}>{v}</span> : '—'),
            },
          ]}
        />
      )}
    </Modal>
  );
};

export default DistributionStatusModal;
